import {  Component, OnInit, OnDestroy , inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AuthService } from "../../core/services/auth.service";
import { getHttpErrorMessage } from "../../shared/utils/http-error.util";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { TuiAutoFocus, TuiDay, TuiDayRange } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiButton,
  TuiDialog,
  TuiDropdown,
  TuiError,
  TuiHint,
  TuiLoader,
} from "@taiga-ui/core";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ShiftService } from "../../core/services/shift.service";
import { DatePipe } from "@angular/common";
import {
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiUnfinishedValidator,
} from "@taiga-ui/kit";
import { catchError, of, Subscription, switchMap, throwError, forkJoin } from "rxjs";
import { ClientService } from "../../core/services/client.service";
import { LeaveService } from "../../core/services/leave.service";
import { FeeService } from "../../core/services/fee.service";
import { NurseService } from "../../core/services/nurse.service";
import { ChargesService } from "../../core/services/charges.service";
import { SalaryService } from "../../core/services/salary.service";
import { ProcedureRecordService } from "../../core/services/procedure-record.service";
import { CheckinComponent } from "../checkin/checkin.component";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";
import {
  numericValidator as sharedNumericValidator,
  phoneValidator,
} from "../../shared/validators/form-validators";
import {
  countInvalidFields,
  focusFirstInvalid,
} from "../../shared/utils/form-focus";
import { loadListState, saveListState } from "../../shared/utils/list-state";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";
import { getEmployeeNumber } from "../../model/interface/nurse";

@Component({
  selector: "app-duty-roster",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    CommonModule,
    ReactiveFormsModule,
    TuiTable,
    TuiButton,
    TuiInputDateRangeModule,
    ReactiveFormsModule,
    FormsModule,
    TuiInputModule,
    TuiDialog,
    TuiHint,
    TuiComboBoxModule,
    TuiTextfieldControllerModule,
    TuiStringifyContentPipe,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiDropdown,
    TuiFieldErrorPipe,
    TuiError,
    TuiTextareaModule,
    TuiInputDateModule,
    CheckinComponent,
  ],
  templateUrl: "./duty-roster.component.html",
  styleUrls: ["./duty-roster.component.scss"],
})
export class DutyRosterComponent implements OnInit, OnDestroy {
  dutyRosterData: any[] = [];
  clientSequenceNumbers: { [clientId: string]: number } = {};
  procedures: any[] = [];
  loading = false;
  popupLoading = false;
  shiftErrorMessage: string | null = null;
  dateRange: Date[] = [];
  isDetailedView: boolean = true;
  /**
   * Soft-warn flexibility: when on, the date-range query also returns
   * archived (soft-deleted) shifts so the user can restore them. The
   * row-level "Restore" button is shown only on rows where archived=true.
   */
  includeArchived: boolean = false;
  startDate: any;
  endDate: any;
  shiftForm: FormGroup;
  clientForm: FormGroup;
  /** Eligibility-filtered raw client list; `clients` is the further client-type-scoped view shown in the combo. */
  allClients: any[] = [];
  clients: any[] = [];
  currentShiftData: any = null;
  originalShiftRange: TuiDayRange | null = null;
  originalBlockShifts: any[] = [];
  nurses: any[] = [];
  selectedFilterClient: any = null;
  selectedFilterNurse: any = null;
  selectedShiftId: string | null = null;
  selectedClientId: string | null = null;
  registrationOptions: string[] = ["Life Time", "One Year"];
  /** Shown on Update Client when a registration type is selected (read-only). */
  displayedRegistrationFee: number | null = null;
  private currentRegistrationFee = 0;
  private currentLifetimeRegistrationFee = 0;
  private clientStoredRegistrationFee: number | null = null;
  private originalClientRegistrationType: string | null = null;
  isNationalHospital = false;
  isUnregisteredClient = false;
  /** True when the selected clientType is "Inward" - drives dynamic field visibility / validators (mirrors Add Shift). */
  isInwardClient = false;
  /**
   * Guard so loading an existing shift does not trigger the
   * "clear client when clientType changes" branch in the value-changes
   * subscription. Without this we'd patch the clientType from the loaded
   * shift, the subscription would immediately wipe the just-patched
   * client, and the dialog would open with an empty client picker.
   */
  private suppressClientReset = false;
  loggedInUserCanEdit = false;
  loggedInUserCanEditShift = false;

  /**
   * Patient Type options mirror the Add Shift (check-in) form so the
   * Update Shift dialog reacts the same way: filters the client list,
   * toggles Inward-specific fields, and adjusts validators.
   */
  protected readonly clientTypeOptions: { label: string; value: string }[] = [
    { label: "Registered", value: "Registered" },
    { label: "Non Registered (With Home Nursing)", value: "Unregistered" },
    { label: "Inward", value: "Inward" },
  ];

  protected stringifyClientType = (
    item: { label: string; value: string } | null,
  ): string => (item ? item.label : "");
  private scrollPosition = { x: 0, y: 0 };
  private pendingScrollTarget: { clientID: string; dateStr: string } | null = null;

  sortBy: { value: string; label: string } | null = {
    value: "shiftCreatedDate",
    label: "Shift / Created",
  };
  readonly sortOptions: { value: string; label: string }[] = [
    { value: "shiftCreatedDate", label: "Shift / Created" },
    { value: "refNo", label: "Client Reg.No (Newest First)" },
    { value: "shiftUpdatedDate", label: "Shift / Updated" },
  ];

  /**
   * Column-width slider (toolbar) - drives `--col-width` CSS var on the
   * table container, which sizes date columns / shift cards. 20-200px.
   */
  columnWidthPx = 200;

  /** Per-client expand/collapse state for the Client Details cell. */
  private expandedClientIds = new Set<string>();
  allExpanded = false;

  private getFirstAndLastDayOfCurrentMonth(): TuiDayRange {
    const now = new Date();

    const firstDay = new TuiDay(now.getFullYear(), now.getMonth(), 1);

    // Get the last day of the current month correctly
    const lastDate = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const lastDay = new TuiDay(now.getFullYear(), now.getMonth(), lastDate);
    this.startDate = this.formatTuiDay(firstDay);
    this.endDate = this.formatTuiDay(lastDay);
    return new TuiDayRange(firstDay, lastDay);
  }

  protected readonly dateForm = new FormGroup({
    testValue: new FormControl(this.getFirstAndLastDayOfCurrentMonth()),
  });

  private subscription: Subscription;
  private authService = inject(AuthService);

  constructor(
    private shiftService: ShiftService,
    private alerts: TuiAlertService,
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private clientService: ClientService,
    private feeService: FeeService,
    private nurseService: NurseService,
    private procedureService: ProcedureRecordService,
    private chargesService: ChargesService,
    private salaryService: SalaryService,
    private leaveService: LeaveService,
  ) {
    this.subscription = this.dateForm
      .get("testValue")!
      .valueChanges.subscribe((value) => {
        if (value) {
          this.startDate = this.formatTuiDay(value.from);
          this.endDate = this.formatTuiDay(value.to);

          console.log("Selected Date Range:", value);
          console.log("Formatted Start Date:", this.startDate);
          console.log("Formatted End Date:", this.endDate);
        }
      });

    this.shiftForm = this.fb.group({
      clientType: [this.clientTypeOptions[0], Validators.required],
      client: [""],
      nurse: ["", Validators.required],
      address: [{ value: "", disabled: true }],
      mealOption: ["", Validators.required],
      typeOption: ["", Validators.required],
      roster: [null, Validators.required],
      rosterType: ["", Validators.required],
      startDateRange: [null, Validators.required],
      charge: [null, Validators.required],
      salary: [null, Validators.required],
      // name: [""],
      // mobile: [""],
      room: [""],
      bhtNumber: [""],
      notes: [""],
    });

    // Soft-warn: name + registrationType are the only truly required fields
    // for the in-roster client edit dialog. Everything else nudges the user
    // via <app-recommended-hint> but never blocks the save.
    this.clientForm = this.fb.group({
      _id: [null],
      name: ["", [Validators.required]],
      age: ["", [sharedNumericValidator]],
      guardianName: [""],
      guardianNIC: [""],
      guardianContactNo: ["", [Validators.maxLength(20), phoneValidator]],
      guardianAddress: [""],
      email: ["", [Validators.email]],
      dateOfBirth: [null],
      registrationType: ["", Validators.required],
      expiryDate: [null],
      currentLocation: [""],
      registrationDate: [null],
      // refNo: ["", Validators.required],
      active: [true],
      profilePic: [""],
      currentCondition: [""],
    });

  }

  protected open = false;
  protected openClientBox = false;
  protected scheduleDialogOpen = false;
  /** Stacked confirm over Update Shift: Yes deletes, Cancel returns to that form. */
  protected confirmDeleteDialogOpen = false;

  /**
   * Roster card click: archived shifts stay on the grid for visibility / Restore
   * only — they must not open the Update Shift dialog (GET shift excludes
   * archived rows and edits are blocked server-side after restore workflow).
   */
  onRosterShiftCardClick(shift: any): void {
    if (shift?.archived) {
      const msg = this.loggedInUserCanEditShift
        ? "This shift is archived. Restore it on the roster, then you can open it to edit."
        : "This shift is archived. Someone with shift update access can restore it if needed.";
      this.alerts
        .open(msg, { label: "Archived shift", appearance: "warning" })
        .subscribe();
      return;
    }
    this.showDialog(shift._id);
  }

  protected showDialog(shiftId: string): void {
    if (!this.loggedInUserCanEditShift) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    // Store scroll position before opening dialog
    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
      this.scrollPosition = {
        x: tableContainer.scrollLeft,
        y: tableContainer.scrollTop
      };
    }
    this.selectedShiftId = shiftId;
    this.loadShiftData(shiftId);
    this.open = true;
  }

  protected showClientDialog(clientId: string): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    // Store scroll position before opening dialog
    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
      this.scrollPosition = {
        x: tableContainer.scrollLeft,
        y: tableContainer.scrollTop
      };
    }
    this.selectedClientId = clientId;
    this.loadClientData(clientId);
    this.openClientBox = true;
  }

  protected openScheduleDialog(): void {
    this.scheduleDialogOpen = true;
  }

  private readonly listStateKey = "duty-roster-list";

  ngOnInit(): void {
    document.documentElement.classList.add('no-page-scroll');
    document.body.classList.add('no-page-scroll');
    this.restoreListState();
    this.fetchDutyRoster();
    this.loadClients();
    this.loadNurse();
    this.loadCurrentFees();
    this.checkUserPermission();

    this.shiftForm.get("typeOption")?.valueChanges.subscribe((value: any) => {
      // Guard: clearing the field emits null/"" - avoid null-deref on .items.
      if (!value) return;
      if (value.items === "NHP (National Hospital)") {
        this.isNationalHospital = true;
        this.shiftForm.get("address")?.setValue("NHP (National Hospital)");
        this.shiftForm.get("address")?.disable();
      } else if (value.items === "Normal" || value.items === "Baby Care") {
        this.shiftForm.get("address")?.enable();
      }
    });

    this.shiftForm.get("client")?.valueChanges.subscribe((value: any) => {
      // Operator-precedence fix: previously `value && value.name == 'Unregistered Client' || this.isNationalHospital`
      // evaluated as `(value && name === '...') || isNationalHospital`, which
      // marked every shift as unregistered as soon as NHP was selected.
      // Group both client-driven conditions explicitly.
      const clientLooksUnregistered =
        !!value &&
        (value.name === "Unregistered Client" ||
          value.clientType === "Unregistered");
      this.isUnregisteredClient =
        clientLooksUnregistered || this.isNationalHospital;

      // Auto-populate Meal Option for Inward clients (client carries preferred meal).
      if (value?.clientType === "Inward") {
        const preferred = String(value.meal || "").trim();
        if (preferred) {
          const match = this.mealOptions.find(
            (opt) => String(opt.items).toLowerCase() === preferred.toLowerCase(),
          );
          if (match && !this.shiftForm.get("mealOption")?.value) {
            this.shiftForm.get("mealOption")?.setValue(match);
          }
        }
      }
    });

    this.shiftForm.get("roster")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "24 hours shift") {
        const withMealOption = this.mealOptions.find(
          (opt) => opt.items === "With Meal"
        );
        const withRosterType = this.rosterType.find(
          (opt) => opt.items === "Day Shift"
        );
        this.shiftForm.get("mealOption")?.setValue(withMealOption);
        this.shiftForm.get("mealOption")?.disable();
        this.shiftForm.get("rosterType")?.setValue(withRosterType);
        this.shiftForm.get("rosterType")?.disable();
      } else if (value && value.items === "12 hours shift") {
        this.shiftForm.get("mealOption")?.enable();
        this.shiftForm.get("rosterType")?.enable();

      }
    });

    // React to client type changes - filter the client picker and toggle
    // Inward-specific layout, mirroring the Add Shift (check-in) form so
    // the Update Shift dialog stays in sync with the rest of the app.
    this.shiftForm.get("clientType")?.valueChanges.subscribe((value: any) => {
      this.applyClientTypeFilter(value?.value);
      if (!this.suppressClientReset) {
        this.shiftForm.patchValue({ client: "" }, { emitEvent: false });
      }
    });

    this.clientForm
      .get("registrationType")
      ?.valueChanges.subscribe((regType) => {
        if (regType === "One Year") {
          const regDate = this.clientForm.get("registrationDate")?.value;
          let calculatedExpiryDate;
          if (regDate) {
            const nativeDate =
              regDate instanceof Date ? regDate : regDate.toLocalNativeDate();
            const expiry = new Date(nativeDate);
            expiry.setFullYear(expiry.getFullYear() + 1);
            calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          } else {
            const expiry = new Date();
            expiry.setFullYear(expiry.getFullYear() + 1);
            calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          }
          this.clientForm.get("expiryDate")?.setValue(calculatedExpiryDate);
        } else if (regType === "Life Time") {
          this.clientForm.get("expiryDate")?.setValue(null);
        }
        this.applyClientRegistrationFeeDisplay();
      });

    this.clientForm
      .get("registrationDate")
      ?.valueChanges.subscribe((regDate) => {
        const regType = this.clientForm.get("registrationType")?.value;
        if (regType === "One Year" && regDate) {
          const nativeDate =
            regDate instanceof Date ? regDate : regDate.toLocalNativeDate();
          const expiry = new Date(nativeDate);
          expiry.setFullYear(expiry.getFullYear() + 1);
          const calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          this.clientForm.get("expiryDate")?.setValue(calculatedExpiryDate);
        }
      });
  }

  private formatTuiDay(tuiDay: TuiDay): string {
    return `${tuiDay.year}-${String(tuiDay.month + 1).padStart(
      2,
      "0"
    )}-${String(tuiDay.day).padStart(2, "0")}`;
  }

  updateConditionalValidators() {
    // Only the legacy name / mobile fields are gated by isUnregisteredClient
    // here. Room / BHT Number are Inward-only and stay optional - they
    // are not surfaced in the template for any other client type, so
    // requiring them on Unregistered shifts would silently block save.
    const fields = ["name", "mobile"];

    fields.forEach((field) => {
      const control = this.shiftForm.get(field);
      if (this.isUnregisteredClient) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });
  }

  findContiguousShiftRange(clickedShift: any, clientShifts: any[]): TuiDayRange {
    const nurseId = clickedShift.nurseID?._id || clickedShift.nurseID;
    const rosterType = clickedShift.roosterType;
    
    const clickedDate = new Date(clickedShift.date);
    clickedDate.setHours(0, 0, 0, 0);

    const candidateShifts = clientShifts.filter(s => {
      const sNurseId = s.nurseID?._id || s.nurseID;
      return sNurseId === nurseId && s.roosterType === rosterType && !s.archived;
    });

    const findShiftOnDate = (date: Date) => {
      const dateStr = this.formatDateToYYYYMMDD(date);
      return candidateShifts.find(s => this.formatDateToYYYYMMDD(s.date) === dateStr);
    };

    let start = clickedDate;
    let end = clickedDate;

    // Search backward
    let current = new Date(clickedDate);
    while (true) {
      current.setDate(current.getDate() - 1);
      const shiftOnPrevDate = findShiftOnDate(current);
      if (shiftOnPrevDate) {
        start = new Date(current);
      } else {
        break;
      }
    }

    // Search forward
    current = new Date(clickedDate);
    while (true) {
      current.setDate(current.getDate() + 1);
      const shiftOnNextDate = findShiftOnDate(current);
      if (shiftOnNextDate) {
        end = new Date(current);
      } else {
        break;
      }
    }

    return new TuiDayRange(
      TuiDay.fromLocalNativeDate(start),
      TuiDay.fromLocalNativeDate(end)
    );
  }

  getContiguousShifts(clickedShift: any, clientShifts: any[]): any[] {
    const nurseId = clickedShift.nurseID?._id || clickedShift.nurseID;
    const rosterType = clickedShift.roosterType;
    
    const clickedDate = new Date(clickedShift.date);
    clickedDate.setHours(0, 0, 0, 0);

    const candidateShifts = clientShifts.filter(s => {
      const sNurseId = s.nurseID?._id || s.nurseID;
      return sNurseId === nurseId && s.roosterType === rosterType && !s.archived;
    });

    const findShiftOnDate = (date: Date) => {
      const dateStr = this.formatDateToYYYYMMDD(date);
      return candidateShifts.find(s => this.formatDateToYYYYMMDD(s.date) === dateStr);
    };

    const resultShifts: any[] = [clickedShift];

    // Search backward
    let current = new Date(clickedDate);
    while (true) {
      current.setDate(current.getDate() - 1);
      const shiftOnPrevDate = findShiftOnDate(current);
      if (shiftOnPrevDate) {
        if (!resultShifts.some(x => x._id === shiftOnPrevDate._id)) {
          resultShifts.push(shiftOnPrevDate);
        }
      } else {
        break;
      }
    }

    // Search forward
    current = new Date(clickedDate);
    while (true) {
      current.setDate(current.getDate() + 1);
      const shiftOnNextDate = findShiftOnDate(current);
      if (shiftOnNextDate) {
        if (!resultShifts.some(x => x._id === shiftOnNextDate._id)) {
          resultShifts.push(shiftOnNextDate);
        }
      } else {
        break;
      }
    }

    return resultShifts;
  }

  checkShiftTouchesLeaveDay(shiftDate: Date, roosterType: string, leaveDate: Date, rooster?: number): boolean {
    const leaveDay = new Date(leaveDate);
    leaveDay.setHours(0, 0, 0, 0);
    const sDay = new Date(shiftDate);
    sDay.setHours(0, 0, 0, 0);

    const isSameCalendarDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    if (isSameCalendarDay(sDay, leaveDay)) {
      return true;
    }

    const isNightShift = roosterType === "Night Shift";
    const is24HourShift = rooster === 24 || roosterType === "24 hours shift";

    if (isNightShift || is24HourShift) {
      const prevDay = new Date(leaveDay);
      prevDay.setDate(prevDay.getDate() - 1);
      if (isSameCalendarDay(sDay, prevDay)) {
        return true;
      }
    }

    return false;
  }

  loadShiftData(shiftId: string): void {
    this.popupLoading = true;
    this.shiftService
      .getShiftById(shiftId)
      .pipe(
        catchError((error) => {
          console.error("Error loading shift data:", error);
          this.popupLoading = false;
          return of(null);
        })
      )
      .subscribe((response: any | null) => {
        if (response && response.data) {
          this.currentShiftData = response.data;
          if (response.data.archived) {
            this.alerts
              .open(
                "This shift is archived. Restore it on the roster before editing.",
                { label: "Archived shift", appearance: "warning" },
              )
              .subscribe();
            this.open = false;
            this.selectedShiftId = null;
            this.popupLoading = false;
            return;
          }
          // Find the client and the shift in dutyRosterData to calculate the full contiguous range
          let startDateRange: TuiDayRange | null = null;
          this.originalBlockShifts = [];
          if (this.dutyRosterData) {
            let foundClient: any = null;
            let foundShift: any = null;
            for (const client of this.dutyRosterData) {
              if (client.shifts) {
                const s = client.shifts.find((x: any) => x._id === shiftId);
                if (s) {
                  foundClient = client;
                  foundShift = s;
                  break;
                }
              }
            }
            if (foundClient && foundShift) {
              startDateRange = this.findContiguousShiftRange(foundShift, foundClient.shifts);
              this.originalBlockShifts = this.getContiguousShifts(foundShift, foundClient.shifts);
            }
          }

          if (!startDateRange) {
            const startDate = response.data.date
              ? TuiDay.fromLocalNativeDate(new Date(response.data.date))
              : null;
            startDateRange = startDate ? new TuiDayRange(startDate, startDate) : null;
          }

          if (this.originalBlockShifts.length === 0 && response.data) {
            this.originalBlockShifts = [response.data];
          }

          this.originalShiftRange = startDateRange;

          // Set Patient Type first so the field-visibility flags
          // (`isInwardClient`, address/typeOption validators, filtered
          // `clients` list) are correct before the rest of the form
          // patches in. `suppressClientReset` keeps the subscription
          // from clearing the just-patched client.
          // The control is intentionally disabled on the Update Shift
          // form (client type is immutable post-creation) - we briefly
          // enable it so `setValue` triggers the valueChanges that
          // drives the filter, then re-disable it below.
          const clientTypeOption = this.resolveClientTypeOption(
            response.data.clientID,
          );
          const clientTypeCtrl = this.shiftForm.get("clientType");
          clientTypeCtrl?.enable({ emitEvent: false });
          this.suppressClientReset = true;
          clientTypeCtrl?.setValue(clientTypeOption);
          this.suppressClientReset = false;

          const patchData = {
            nurse: response.data.nurseID,
            client: response.data.clientID,
            address: response.data.address,
            mealOption: this.mealOptions.find(
              (option) => option.items === response.data.mealOption
            ),
            typeOption: this.typeOptions.find(
              (option) => option.items === response.data.shiftType
            ),
            roster: this.rosterItems.find(
              (item) => item.value === response.data.rooster
            ),
            rosterType: this.rosterType.find(
              (option) => option.items === response.data.roosterType
            ),
            charge: response.data.charges,
            salary: response.data.salary,
            startDateRange: startDateRange,
            notes: response.data.notes,
            name: response.data.name,
            mobile: response.data.mobile,
            room: response.data.room,
            bhtNumber: response.data.bhtNumber,
          };

          this.shiftForm.patchValue(patchData);

          // Lock the Patient Type field on update: ops can see what type
          // the shift was scheduled against, but changing it would
          // invalidate the linked client + downstream charges.
          clientTypeCtrl?.disable({ emitEvent: false });
        } else {
          this.alerts
            .open(
              "This shift could not be loaded. It may be archived or removed.",
              { label: "Unable to open shift", appearance: "warning" },
            )
            .subscribe();
          this.open = false;
          this.selectedShiftId = null;
        }
        this.popupLoading = false;
      });
  }

  loadClientData(clientId: string): void {
    this.popupLoading = true;
    this.clientService
      .getOneClientById(clientId)
      .pipe(
        catchError((error) => {
          console.error("Error loading client data:", error);
          this.popupLoading = false;
          return of(null);
        })
      )
      .subscribe(
        (
          response: {
            profilePic?: string;
            registrationDate?: TuiDay;
            dateOfBirth?: TuiDay;
            expiryDate?: TuiDay;
            registrationType?: string;
            registrationFee?: number;
          } | null
        ) => {
          if (response) {
            const data = response as Record<string, unknown>;
            this.clientStoredRegistrationFee =
              typeof data["registrationFee"] === "number"
                ? (data["registrationFee"] as number)
                : null;
            this.originalClientRegistrationType =
              typeof data["registrationType"] === "string"
                ? data["registrationType"]
                : null;

            (
              ["registrationDate", "dateOfBirth", "expiryDate"] as const
            ).forEach((field) => {
              if (response[field]) {
                response[field] = TuiDay.fromLocalNativeDate(
                  new Date(response[field]!.toString())
                );
              }
            });
            this.clientForm.patchValue(response);
            this.applyClientRegistrationFeeDisplay();
          }
          this.popupLoading = false;
        }
      );
  }

  private loadCurrentFees(): void {
    this.feeService.getCurrentFee().subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.currentRegistrationFee = res.data.registrationFee || 0;
          this.currentLifetimeRegistrationFee =
            res.data.lifetimeRegistrationFee || 0;
          this.applyClientRegistrationFeeDisplay();
        }
      },
      error: (error) => {
        console.error("Failed to load current fees:", error);
      },
    });
  }

  /** Mirrors add-client fee rules: stored fee when type unchanged, else system fee. */
  private applyClientRegistrationFeeDisplay(): void {
    const type = this.clientForm.get("registrationType")?.value;
    if (!type) {
      this.displayedRegistrationFee = null;
      return;
    }

    const typeUnchanged =
      this.originalClientRegistrationType != null &&
      type === this.originalClientRegistrationType;
    const useStored =
      typeUnchanged &&
      this.clientStoredRegistrationFee != null &&
      this.clientStoredRegistrationFee > 0;

    if (type === "Life Time") {
      this.displayedRegistrationFee = useStored
        ? this.clientStoredRegistrationFee!
        : this.currentLifetimeRegistrationFee;
    } else if (type === "One Year") {
      this.displayedRegistrationFee = useStored
        ? this.clientStoredRegistrationFee!
        : this.currentRegistrationFee;
    } else {
      this.displayedRegistrationFee = null;
    }
  }

  loadClients(): void {
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        const baseList = this.includeInactiveClients
          ? clients
          : clients.filter(
            (c: any) => !c?.archived && c?.active !== false,
          );
        this.allClients = baseList.filter((c: any) =>
          this.isEligibleForDutyRoster(c),
        );
        this.applyClientTypeFilter(
          this.shiftForm.get("clientType")?.value?.value,
        );
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  /**
   * Scope the client combo to the currently selected Patient Type and
   * toggle Inward-specific layout / validators. Mirrors the Add Shift
   * form so the Update Shift dialog behaves the same way.
   */
  private applyClientTypeFilter(clientTypeValue: string | undefined): void {
    const type = clientTypeValue || "Registered";
    this.isInwardClient = type === "Inward";

    const typeOption = this.shiftForm.get("typeOption");
    const address = this.shiftForm.get("address");
    const notes = this.shiftForm.get("notes");

    if (this.isInwardClient) {
      typeOption?.clearValidators();
      typeOption?.setValue("", { emitEvent: false });
      address?.disable({ emitEvent: false });
      address?.setValue("", { emitEvent: false });
      notes?.setValue("", { emitEvent: false });
    } else {
      typeOption?.setValidators(Validators.required);
      // Re-enable address by default; the typeOption subscription will
      // disable it again if "NHP (National Hospital)" is selected.
      address?.enable({ emitEvent: false });
    }
    typeOption?.updateValueAndValidity({ emitEvent: false });

    this.clients = this.allClients.filter((c: any) => {
      const ct = c?.clientType || "Registered";
      if (type === "Registered") return ct === "Registered";
      if (type === "Unregistered") {
        return ct === "Unregistered" && c?.nursingType === "With Home Nursing";
      }
      if (type === "Inward") return ct === "Inward";
      return true;
    });
  }

  /**
   * Match the loaded shift's client to one of the Patient Type options
   * so the new combo box can pre-select the right entry. Unregistered
   * "Without Home Nursing" still maps to the Unregistered option - the
   * combo lets ops correct the record even if the client itself is
   * outside duty-roster eligibility.
   */
  private resolveClientTypeOption(
    client: any,
  ): { label: string; value: string } {
    const raw = client?.clientType || "Registered";
    return (
      this.clientTypeOptions.find((opt) => opt.value === raw) ||
      this.clientTypeOptions[0]
    );
  }

  /**
   * Soft-warn flexibility: when on, the client picker also includes
   * inactive/archived clients (still gated by duty-roster eligibility).
   * Used only by the inline shift dialog's client picker.
   */
  includeInactiveClients: boolean = false;

  toggleIncludeInactiveClients(value: boolean): void {
    this.includeInactiveClients = value;
    this.loadClients();
  }

  isSelectedShiftClientInactive(): boolean {
    const ctrl = this.shiftForm.get("client");
    const id =
      ctrl && typeof ctrl.value === "object"
        ? ctrl.value?._id
        : ctrl?.value;
    if (!id) return false;
    const client = this.clients.find((c: any) => c._id === id);
    if (!client) return false;
    return Boolean(client.archived) || client.active === false;
  }

  /**
   * Duty Roster eligibility:
   *   - Registered (any registrationType)
   *   - Unregistered with "With Home Nursing"
   *   - Inward
   * Unregistered "Without Home Nursing" is excluded.
   */
  private isEligibleForDutyRoster(client: any): boolean {
    const type = client?.clientType || "Registered";
    if (type === "Registered" || type === "Inward") return true;
    if (type === "Unregistered") {
      return client?.nursingType === "With Home Nursing";
    }
    return false;
  }

  loadNurse(): void {
    this.nurseService.getAllNurses().subscribe({
      next: (response: any) => {
        const nurses = Array.isArray(response) ? response : [];
        this.nurses = nurses.filter((n: any) => !n?.archived && n?.active !== false);
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  updateShift(): void {
    this.shiftErrorMessage = null;
    if (!this.selectedShiftId) {
      return;
    }

    this.updateConditionalValidators();
    if (this.shiftForm.invalid) {
      const count = countInvalidFields(this.shiftForm);
      focusFirstInvalid(this.shiftForm);
      const msg = `${count} field${count === 1 ? "" : "s"} need attention before saving.`;
      this.shiftErrorMessage = msg;
      this.alerts
        .open(
          msg,
          { label: "Form incomplete", appearance: "warning" },
        )
        .subscribe();
      return;
    }

    this.popupLoading = true;
    const range = this.shiftForm.get('startDateRange')?.value as TuiDayRange;
    if (!range) {
      this.popupLoading = false;
      return;
    }

    const start = range.from;
    const end = range.to;
    const nurseID = this.shiftForm.get('nurse')?.value?._id || this.shiftForm.get('nurse')?.value;

    const queryStart = start.append({ day: -1 });
    const queryEnd = end.append({ day: 1 });

    this.leaveService.getByDateRange(
      this.formatTuiDay(queryStart),
      this.formatTuiDay(queryEnd),
      nurseID
    ).subscribe({
      next: (res: any) => {
        const leaves = res?.data || [];
        const newDates: TuiDay[] = [];
        let curr = start;
        while (curr.dayBefore(end) || curr.daySame(end)) {
          newDates.push(curr);
          curr = curr.append({ day: 1 });
        }

        const shiftPayloadTemplate = {
          salary: this.shiftForm.get('salary')?.value,
          charges: this.shiftForm.get('charge')?.value,
          rooster: this.shiftForm.get("roster")?.value?.value,
          roosterType: this.shiftForm.get("rosterType")?.value?.items,
          nurseID: nurseID,
          clientID: this.shiftForm.get('client')?.value?._id || this.shiftForm.get('client')?.value,
          address: this.shiftForm.get('address')?.value,
          mealOption: this.shiftForm.get('mealOption')?.value?.items,
          shiftType: this.isInwardClient
            ? (this.currentShiftData?.shiftType || "Normal")
            : (this.shiftForm.get('typeOption')?.value?.items || "Normal"),
          notes: this.shiftForm.get('notes')?.value,
          name: this.shiftForm.get('name')?.value,
          mobile: this.shiftForm.get('mobile')?.value,
          room: this.shiftForm.get('room')?.value,
          bhtNumber: this.shiftForm.get('bhtNumber')?.value,
          isUnregisteredPatient: this.currentShiftData?.isUnregisteredPatient || false,
          availabilityUpdated: false,
        };

        const conflictingDates: string[] = [];
        for (const tuiDay of newDates) {
          const shiftDate = new Date(tuiDay.year, tuiDay.month, tuiDay.day);
          for (const leave of leaves) {
            const from = new Date(leave.fromDate);
            from.setHours(0, 0, 0, 0);
            const to = new Date(leave.toDate);
            to.setHours(0, 0, 0, 0);
            
            let cursor = new Date(from);
            while (cursor <= to) {
              if (this.checkShiftTouchesLeaveDay(shiftDate, shiftPayloadTemplate.roosterType, cursor, shiftPayloadTemplate.rooster)) {
                conflictingDates.push(this.formatTuiDay(tuiDay));
                break;
              }
              cursor.setDate(cursor.getDate() + 1);
            }
          }
        }

        if (conflictingDates.length > 0) {
          this.popupLoading = false;
          this.shiftErrorMessage = `Unable to schedule shifts for the selected nurse on the following dates as the nurse is on leave: ${conflictingDates.join(", ")}`;
          return;
        }

        // Proceed with updates/creations/deletions...
        const updatesToPerform: { id: string; payload: any }[] = [];
        const creationsToPerform: any[] = [];
        const deletionsToPerform: string[] = [];

        // 1) Find which dates are new creations vs updates
        newDates.forEach(tuiDay => {
          const dateStr = this.formatTuiDay(tuiDay);
          const matchingShift = this.originalBlockShifts.find(s => this.formatDateToYYYYMMDD(s.date) === dateStr);
          if (matchingShift) {
            updatesToPerform.push({
              id: matchingShift._id,
              payload: {
                ...shiftPayloadTemplate,
                date: dateStr
              }
            });
          } else {
            creationsToPerform.push({
              ...shiftPayloadTemplate,
              date: dateStr
            });
          }
        });

        // 2) Find which existing block shifts are deleted (only when resizing an overlapping block)
        const hasOverlapWithOriginalBlock = this.originalBlockShifts.some(s => {
          const sDateStr = this.formatDateToYYYYMMDD(s.date);
          return newDates.some(tuiDay => this.formatTuiDay(tuiDay) === sDateStr);
        });

        if (hasOverlapWithOriginalBlock) {
          this.originalBlockShifts.forEach(s => {
            const sDateStr = this.formatDateToYYYYMMDD(s.date);
            const stillExists = newDates.some(tuiDay => this.formatTuiDay(tuiDay) === sDateStr);
            if (!stillExists) {
              deletionsToPerform.push(s._id);
            }
          });
        }

        const observables: any[] = [];

        // Add updates to forkJoin
        updatesToPerform.forEach(u => {
          observables.push(
            this.shiftService.updateShift(u.id, u.payload).pipe(
              catchError(err => of({ error: err, type: 'update', id: u.id }))
            )
          );
        });

        // Add creations to forkJoin
        creationsToPerform.forEach(c => {
          observables.push(
            this.shiftService.createShift(c).pipe(
              catchError(err => of({ error: err, type: 'create', date: c.date }))
            )
          );
        });

        // Add deletions to forkJoin
        deletionsToPerform.forEach(id => {
          observables.push(
            this.shiftService.archiveShift(id).pipe(
              catchError(err => of({ error: err, type: 'delete', id }))
            )
          );
        });

        if (observables.length === 0) {
          this.finishUpdate();
          return;
        }

        forkJoin(observables).subscribe({
          next: (results: any[]) => {
            const errors = results.filter(r => r && r.error);
            if (errors.length > 0) {
              console.error("Some operations failed during shift range reconciliation:", errors);
              const msg = "Range update partially completed with errors.";
              this.shiftErrorMessage = msg;
              this.popupLoading = false;
              this.alerts
                .open(msg, { label: "Warning", appearance: "warning" })
                .subscribe();
            } else {
              this.alerts
                .open("Shift range has been updated successfully.", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.pendingScrollTarget = {
                clientID: shiftPayloadTemplate.clientID,
                dateStr: this.formatTuiDay(start)
              };
              this.finishUpdate();
            }
          },
          error: (err: any) => {
            this.popupLoading = false;
            console.error("Critical error during range update:", err);
            const msg = err.error?.message || "Failed to update shift range.";
            this.shiftErrorMessage = msg;
            this.alerts
              .open(msg, { label: "Error", appearance: "error" })
              .subscribe();
          }
        });
      },
      error: (err: any) => {
        this.popupLoading = false;
        console.error("Failed to check nurse leave conflicts:", err);
        const msg = err.error?.message || "Failed to validate leaves for the nurse.";
        this.shiftErrorMessage = msg;
      }
    });
  }

  private finishUpdate(): void {
    this.fetchDutyRoster();
    this.popupLoading = false;
    this.open = false;
    this.shiftErrorMessage = null;
    if (!this.pendingScrollTarget) {
      this.restoreScrollPosition();
    }
  }

  deleteShift(): void {
    if (!this.selectedShiftId) {
      console.error("No shift id selected for deletion.");
      return;
    }
    this.confirmDeleteDialogOpen = true;
  }

  onConfirmDeleteYes(): void {
    this.confirmDeleteDialogOpen = false;
    this.archiveShiftAfterConfirm();
  }

  onConfirmDeleteCancel(): void {
    this.confirmDeleteDialogOpen = false;
  }

  /**
   * Performs archive/delete after the user confirms in the dialog.
   * Uses the archive endpoint so the action is captured in the audit log.
   */
  private archiveShiftAfterConfirm(): void {
    if (!this.selectedShiftId) {
      return;
    }

    this.popupLoading = true;
    this.shiftService.archiveShift(this.selectedShiftId).subscribe(
      (res: any) => {
        this.alerts
          .open("Shift has been deleted successfully.", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.fetchDutyRoster();
        this.popupLoading = false;
        this.open = false;
      },
      (error: any) => {
        this.alerts
          .open("Failed to delete shift.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
        this.popupLoading = false;
        console.error(error);
      }
    );
  }

  updateClient(): void {
    if (this.clientForm.invalid) {
      const count = countInvalidFields(this.clientForm);
      focusFirstInvalid(this.clientForm);
      this.alerts
        .open(
          `${count} field${count === 1 ? "" : "s"} need attention before saving.`,
          { label: "Form incomplete", appearance: "warning" },
        )
        .subscribe();
      return;
    }

    this.popupLoading = true;
    const payload = this.clientForm.getRawValue();

    // For Life Time clients, explicitly set expiryDate to null
    if (payload.registrationType === 'Life Time') {
      payload.expiryDate = null;
    }

    if (this.selectedClientId) {
      this.clientService
        .updateClient(payload)
        .pipe(
          catchError((error) => {
            if (getHttpErrorMessage(error) === "Email is already existing!") {
              this.clientForm
                .get("email")
                ?.setErrors({ duplicateEmail: true });
            }
            if (
              getHttpErrorMessage(error) === "Contact number is already existing!"
            ) {
              this.clientForm
                .get("guardianContactNo")
                ?.setErrors({ duplicateEmail: true });
            }
            if (getHttpErrorMessage(error) === "Client NIC is already existing!") {
              this.clientForm
                .get("guardianNIC")
                ?.setErrors({ duplicateEmail: true });
            }
            this.popupLoading = false;
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          })
        )
        .subscribe((res: any) => {
          this.popupLoading = false;
          if (res) {
            this.alerts
              .open("Client updated successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.fetchDutyRoster();
            this.popupLoading = false;
            this.openClientBox = false;
            this.restoreScrollPosition();
          }
        });
    }
  }


  /**
   * Display format for the client (a.k.a. Select Client) combo box:
   *   `REG-0001 - Client Name (Patient Name)`
   * Falls back gracefully when client and/or patient name are missing
   * so the option never renders as a dangling separator.
   */
  stringifyClients(client: any): string {
    const format = (c: any): string => {
      const id = formatClientIdentifier(c);
      const name = String(c?.name || "").trim();
      const guardian = String(c?.guardianName || "").trim();
      if (guardian && name) return `${id} - ${guardian} (${name})`;
      if (guardian) return `${id} - ${guardian}`;
      if (name) return `${id} - ${name}`;
      return id;
    };

    if (!client) return "";
    if (typeof client === "object") return format(client);
    const foundClient = this.clients.find((c: any) => c._id === client);
    return foundClient ? format(foundClient) : "";
  }

  stringifyNurses(nurse: any): string {
    if (!nurse) return "";
    if (typeof nurse === "object") {
      return `${getEmployeeNumber(nurse)} - ${nurse.name}`;
    }
    const foundNurse = this.nurses.find((c: any) => c._id === nurse);
    return foundNurse
      ? `${getEmployeeNumber(foundNurse)} - ${foundNurse.name}`
      : "";
  }

  stringifyDropdown(item: { items: string; value: number } | null): string {
    if (!item) return "";
    return item.items;
  }

  protected readonly rosterItems = [
    { items: "12 hours shift", value: 12 },
    { items: "24 hours shift", value: 24 },
  ];

  protected readonly rosterType = [
    { items: "Day Shift" },
    { items: "Night Shift" },
  ];

  protected readonly mealOptions = [
    { items: "With Meal" },
    { items: "Without Meal" },
  ];

  protected readonly typeOptions = [
    { items: "Normal" },
    { items: "Baby Care" },
    { items: "NHP (National Hospital)" },
  ];

  /**
   * Toggle the archived filter and refetch. Used by the "Show archived"
   * switch in the toolbar.
   */
  toggleIncludeArchived(value: boolean): void {
    this.includeArchived = value;
    this.fetchDutyRoster();
  }

  /**
   * Column-width slider handler: updates the CSS variable driving date
   * column / shift-card sizing across the roster table.
   */
  onColumnWidthChange(value: number): void {
    this.columnWidthPx = value;
    this.rememberListState();
  }

  /** Whether a given client's detail cell is currently expanded. */
  isExpanded(client: any): boolean {
    return this.expandedClientIds.has(client._id);
  }

  /** Toggle a single client's Client Details cell between summary and full detail. */
  toggleExpand(client: any): void {
    if (this.expandedClientIds.has(client._id)) {
      this.expandedClientIds.delete(client._id);
    } else {
      this.expandedClientIds.add(client._id);
    }
    this.allExpanded =
      this.dutyRosterData.length > 0 &&
      this.dutyRosterData.every((c: any) => this.expandedClientIds.has(c._id));
  }

  /** Expand or collapse every client row's detail cell at once. */
  toggleExpandAll(): void {
    this.allExpanded = !this.allExpanded;
    if (this.allExpanded) {
      this.dutyRosterData.forEach((c: any) => this.expandedClientIds.add(c._id));
    } else {
      this.expandedClientIds.clear();
    }
  }

  /**
   * Restore a soft-deleted shift. Triggered from the "Restore" action
   * button shown next to archived rows.
   */
  restoreShift(shiftId: string): void {
    if (!shiftId) return;
    this.popupLoading = true;
    this.shiftService.unarchiveShift(shiftId).subscribe({
      next: () => {
        this.alerts
          .open("Shift restored.", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.popupLoading = false;
        this.fetchDutyRoster();
      },
      error: () => {
        this.popupLoading = false;
        this.alerts
          .open("Failed to restore shift.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      startDate: this.startDate,
      endDate: this.endDate,
      includeArchived: this.includeArchived,
      sortBy: this.sortBy,
      isDetailedView: this.isDetailedView,
      columnWidthPx: this.columnWidthPx,
    });
    if (restored.startDate) this.startDate = restored.startDate;
    if (restored.endDate) this.endDate = restored.endDate;
    this.includeArchived = restored.includeArchived;
    if (restored.sortBy) this.sortBy = restored.sortBy;
    this.isDetailedView = restored.isDetailedView;
    if (restored.columnWidthPx) this.columnWidthPx = restored.columnWidthPx;
  }

  private rememberListState(): void {
    saveListState(this.listStateKey, {
      startDate: this.startDate,
      endDate: this.endDate,
      includeArchived: this.includeArchived,
      sortBy: this.sortBy,
      isDetailedView: this.isDetailedView,
      columnWidthPx: this.columnWidthPx,
    });
  }

  fetchDutyRoster(): void {
    // this.rememberListState();
    const startDate = this.startDate;
    const endDate = this.endDate;

    this.dateRange = this.generateDateRange(
      new Date(startDate),
      new Date(endDate)
    );
    console.log("Selected Date :", startDate);
    console.log("Selected Date 2 :", endDate);

    this.loading = true;
    this.shiftService
      .getShiftsByDateRange(startDate, endDate, this.includeArchived)
      .subscribe({
        next: (res: any) => {
          this.dutyRosterData = res.data;
          this.calculateSequenceNumbers();
          this.loading = false;
          if (this.pendingScrollTarget) {
            const target = this.pendingScrollTarget;
            this.pendingScrollTarget = null;
            this.scrollToShift(target.clientID, target.dateStr);
          }
        },
        error: () => {
          this.loading = false;
          this.alerts
            .open("Failed to fetch duty roster data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });

  }

  calculateSequenceNumbers(): void {
    if (!this.dutyRosterData || this.dutyRosterData.length === 0) {
      this.clientSequenceNumbers = {};
      return;
    }
    const sorted = [...this.dutyRosterData].sort((a, b) => {
      const aTime = this.getLatestShiftCreatedAt(a)?.getTime() ?? 0;
      const bTime = this.getLatestShiftCreatedAt(b)?.getTime() ?? 0;
      if (aTime !== bTime) {
        return aTime - bTime;
      }
      const aCreated = (a.clientCreatedAt || a.createdAt) ? new Date(a.clientCreatedAt || a.createdAt).getTime() : 0;
      const bCreated = (b.clientCreatedAt || b.createdAt) ? new Date(b.clientCreatedAt || b.createdAt).getTime() : 0;
      if (aCreated !== bCreated) {
        return aCreated - bCreated;
      }
      const aVal = (a.refNo ?? "").toString();
      const bVal = (b.refNo ?? "").toString();
      return aVal.localeCompare(bVal, undefined, { numeric: true });
    });

    const seqNumbers: { [clientId: string]: number } = {};
    sorted.forEach((client, index) => {
      seqNumbers[client._id] = index + 1;
    });
    this.clientSequenceNumbers = seqNumbers;
  }

  resetSearchAndFilters(): void {
    // Reset to default: current-month range, default sort, hide archived.
    const range = this.getFirstAndLastDayOfCurrentMonth();
    this.dateForm.get("testValue")?.setValue(range);
    this.sortBy = { value: "shiftCreatedDate", label: "Shift / Created" };
    this.includeArchived = false;
    this.selectedFilterClient = null;
    this.selectedFilterNurse = null;
    this.fetchDutyRoster();
  }

  formatClientId(client: any): string {
    return formatClientIdentifier(client);
  }

  getShiftType(startTime: string): string {
    const hour = new Date(startTime).getHours();
    return hour >= 6 && hour < 18 ? "Day Shift" : "Night Shift";
  }

  getStatusClass(status: string): string {
    switch (status) {
      case "Ongoing":
        return "status-ongoing";
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Canceled":
        return "status-canceled";
      case "Absent":
        return "status-absent";
      default:
        return "";
    }
  }

  generateDateRange(startDate: Date, endDate: Date): Date[] {
    const range = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      range.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return range;
  }

  formatDateToYYYYMMDD(date: Date | string): string {
    if (!date) return '';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.substring(0, 10);
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getShiftsForDate(shifts: any[], date: Date): any[] {
    const shiftDate = this.formatDateToYYYYMMDD(date);
    let foundShifts = shifts.filter((shift) => this.formatDateToYYYYMMDD(shift.date) === shiftDate);

    if (this.selectedFilterNurse) {
      const nurseId = typeof this.selectedFilterNurse === 'object'
        ? this.selectedFilterNurse._id
        : this.selectedFilterNurse;
      foundShifts = foundShifts.filter((shift) =>
        !shift.isOffDay && (shift.nurseID === nurseId || shift.nurse === nurseId)
      );
    }

    // If no shifts found for this date, return an off day object
    if (foundShifts.length === 0) {
      return [{
        _id: `off-day-${shiftDate}`,
        nurseName: 'Off Day',
        date: shiftDate,
        isOffDay: true,
        roster: 'Off Day',
        rosterType: 'Off Day',
        mealOption: 'N/A',
        charge: 0,
        salary: 0,
        clientName: 'N/A',
        clientId: null
      }];
    }

    return foundShifts;
  }

  hasOffDayForDate(shifts: any[], date: Date): boolean {
    const shiftDate = this.formatDateToYYYYMMDD(date);
    let foundShifts = shifts.filter((shift) => this.formatDateToYYYYMMDD(shift.date) === shiftDate);

    if (this.selectedFilterNurse) {
      const nurseId = typeof this.selectedFilterNurse === 'object'
        ? this.selectedFilterNurse._id
        : this.selectedFilterNurse;
      foundShifts = foundShifts.filter((shift) =>
        !shift.isOffDay && (shift.nurseID === nurseId || shift.nurse === nurseId)
      );
    }

    return foundShifts.length === 0;
  }

  getFirstShift(client: any): any {
    if (!client.shifts || client.shifts.length === 0) {
      return null;
    }
    return client.shifts[0]; // Returns the first shift
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("CLIENT_UPDATE");
    this.loggedInUserCanEditShift =
      this.authService.hasAnyPermission(["SHIFT_UPDATE", "SHIFT_DELETE"]);
  }

  // Add method to restore scroll position
  private restoreScrollPosition(): void {
    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
      setTimeout(() => {
        tableContainer.scrollLeft = this.scrollPosition.x;
        tableContainer.scrollTop = this.scrollPosition.y;
      }, 0);
    }
  }

  // Add method to handle dialog close
  protected onDialogClose(): void {
    this.restoreScrollPosition();
  }

  protected onShiftCreatedFromDialog(event?: any): void {
    this.scheduleDialogOpen = false;
    if (event && event.clientID && event.dates && event.dates.length > 0) {
      const sortedDates = [...event.dates].sort();
      this.pendingScrollTarget = {
        clientID: event.clientID,
        dateStr: sortedDates[0]
      };
    }
    this.fetchDutyRoster();
  }

  private scrollToShift(clientID: string, dateStr: string): void {
    setTimeout(() => {
      const tableContainer = document.querySelector('.table-container');
      if (!tableContainer) return;

      const targetCell = tableContainer.querySelector(
        `td[data-client-id="${clientID}"][data-date="${dateStr}"]`
      );
      if (targetCell) {
        const containerRect = tableContainer.getBoundingClientRect();
        const cellRect = targetCell.getBoundingClientRect();
        const clientCol = tableContainer.querySelector('.fixed-left');
        const stickyWidth = clientCol ? clientCol.getBoundingClientRect().width : 160;

        const scrollLeftOffset = cellRect.left - containerRect.left + tableContainer.scrollLeft;
        const targetScrollLeft = scrollLeftOffset - stickyWidth - 20;

        tableContainer.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  stringifySortOption(option: { value: string; label: string } | null): string {
    return option ? option.label : "";
  }

  onSortChange(value: { value: string; label: string } | null): void {
    this.sortBy = value;
  }

  getSortedDutyRosterData(): any[] {
    if (!this.dutyRosterData?.length) return [];
    const data = [...this.dutyRosterData];
    const key = this.sortBy?.value ?? "shiftCreatedDate";

    if (key === "refNo") {
      // Sort by Registration Number with newest (most recently created/added) at the top.
      // Fallback to refNo descending when createdAt is missing.
      return data.sort((a, b) => {
        const aCreated = (a.clientCreatedAt || a.createdAt) ? new Date(a.clientCreatedAt || a.createdAt).getTime() : 0;
        const bCreated = (b.clientCreatedAt || b.createdAt) ? new Date(b.clientCreatedAt || b.createdAt).getTime() : 0;
        if (aCreated !== bCreated) return bCreated - aCreated;
        const aVal = (a.refNo ?? "").toString();
        const bVal = (b.refNo ?? "").toString();
        return bVal.localeCompare(aVal, undefined, { numeric: true });
      });
    }

    if (key === "shiftCreatedDate") {
      return data.sort((a, b) => {
        const aMax = this.getLatestShiftCreatedAt(a);
        const bMax = this.getLatestShiftCreatedAt(b);
        return (bMax?.getTime() ?? 0) - (aMax?.getTime() ?? 0);
      });
    }

    if (key === "shiftUpdatedDate") {
      return data.sort((a, b) => {
        const aMax = this.getLatestShiftUpdatedAt(a);
        const bMax = this.getLatestShiftUpdatedAt(b);
        return (bMax?.getTime() ?? 0) - (aMax?.getTime() ?? 0);
      });
    }

    return data;
  }

  getFilteredAndSortedDutyRosterData(): any[] {
    let data = this.getSortedDutyRosterData();

    if (this.selectedFilterClient) {
      const clientId = typeof this.selectedFilterClient === 'object'
        ? this.selectedFilterClient._id
        : this.selectedFilterClient;
      data = data.filter(c => c._id === clientId);
    }

    if (this.selectedFilterNurse) {
      const nurseId = typeof this.selectedFilterNurse === 'object'
        ? this.selectedFilterNurse._id
        : this.selectedFilterNurse;
      data = data.filter(c =>
        c.shifts && c.shifts.some((s: any) =>
          !s.isOffDay && (s.nurseID === nurseId || s.nurse === nurseId)
        )
      );
    }

    return data;
  }

  private getLatestShiftCreatedAt(client: any): Date | null {
    const shifts = client?.shifts ?? [];
    const valid = shifts.filter((s: any) => s && !s.isOffDay && s.createdAt);
    if (valid.length === 0) return null;
    const dates = valid.map((s: any) => new Date(s.createdAt).getTime());
    return new Date(Math.max(...dates));
  }

  private getLatestShiftUpdatedAt(client: any): Date | null {
    const shifts = client?.shifts ?? [];
    const valid = shifts.filter((s: any) => s && !s.isOffDay && (s.updatedAt || s.createdAt));
    if (valid.length === 0) return null;
    const dates = valid.map((s: any) =>
      new Date(s.updatedAt || s.createdAt).getTime()
    );
    return new Date(Math.max(...dates));
  }

  isLastAddedOrUpdated(client: any, shift: any): boolean {
    if (!shift || shift.isOffDay || !shift._id) return false;
    const shifts = client?.shifts ?? [];
    const valid = shifts.filter((s: any) => s && !s.isOffDay && s._id);
    if (valid.length === 0) return false;
    const getTimestamp = (s: any) =>
      new Date(s.updatedAt || s.createdAt || 0).getTime();
    const maxTs = Math.max(...valid.map(getTimestamp));
    return getTimestamp(shift) === maxTs;
  }

  private _sequenceMap = new Map<string, number>();
  private _cachedDataReference: any[] | null = null;

  getSequenceNumber(client: any): number {
    if (!this.dutyRosterData?.length) return 0;

    if (!this._sequenceMap || this._cachedDataReference !== this.dutyRosterData) {
      const sorted = [...this.dutyRosterData].sort((a, b) => {
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (aCreated !== bCreated) return aCreated - bCreated;
        const aVal = (a.refNo ?? "").toString();
        const bVal = (b.refNo ?? "").toString();
        return aVal.localeCompare(bVal, undefined, { numeric: true });
      });

      this._sequenceMap = new Map<string, number>();
      sorted.forEach((item, idx) => {
        this._sequenceMap.set(item._id, idx + 1);
      });
      this._cachedDataReference = this.dutyRosterData;
    }

    return this._sequenceMap.get(client._id) ?? 0;
  }

  getShiftTooltip(client: any, shift: any): string {
    if (!shift) return "";
    if (shift.isOffDay) {
      return `Off Day\nClient: ${client.guardianName || '—'}`;
    }

    const lines: string[] = [];
    lines.push(`Nurse: ${shift.nurseName || 'Unassigned'}`);
    if (shift.roster) {
      lines.push(`Roster: ${shift.roster}`);
    }
    if (shift.roosterType || shift.shiftType) {
      lines.push(`${shift.roosterType || shift.shiftType}`);
    }
    if (client.regType === 'Regular Client') {
      if (shift.name) lines.push(`Patient: ${shift.name}`);
      if (shift.mobile) lines.push(`Mobile: ${shift.mobile}`);
      if (shift.room || shift.bhtNumber) {
        lines.push(`Room/Bed: ${shift.room || '—'} / ${shift.bhtNumber || '—'}`);
      }
    } else if (client.regType === 'Inward') {
      if (shift.room || shift.bhtNumber) {
        lines.push(`Room/Bed: ${shift.room || '—'} / ${shift.bhtNumber || '—'}`);
      }
    }
    return lines.join("\n");
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('no-page-scroll');
    document.body.classList.remove('no-page-scroll');
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}